import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3 } from 'lucide-react';
import { useNotification } from '@kardume/hooks';
import { adminApi, extractErrorMessage } from '@kardume/services';
import type { MerchantAccessProfileDto, MerchantPermissionCode } from '@kardume/types';
import { MERCHANT_PERMISSION_LABELS } from '@kardume/types';
import { PageHeader, Table, TableContainer, Badge, Button, ActionButton, Modal, TextInput, Checkbox } from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import {
  PERMISSION_GROUP_ORDER,
  formatPermissionLabel,
  formatPermissionDescription,
  formatPermissionGroup,
  sortPermissionCodes,
} from './accessControlCatalog';

const ALL_CODES = sortPermissionCodes(Object.keys(MERCHANT_PERMISSION_LABELS) as MerchantPermissionCode[]);

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 12px;
  padding: 1rem 1.25rem;
  span {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textMuted};
  }
  strong {
    display: block;
    margin-top: 0.35rem;
    font-size: 1.6rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.35rem;
  justify-content: flex-end;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const GroupTitle = styled.p`
  margin: 0.75rem 0 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => p.theme.textMuted};
`;

const PermissionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const PermissionCard = styled.div<{ $active: boolean }>`
  text-align: left;
  border: 1px solid ${(p) => (p.$active ? p.theme.primary : p.theme.border)};
  background: ${(p) => (p.$active ? `${p.theme.primary}14` : p.theme.surface)};
  border-radius: 10px;
  padding: 0.75rem;
  cursor: pointer;
  display: flex;
  gap: 0.6rem;
  .body {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .title {
    font-weight: 600;
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  .desc {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
  .code {
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
    font-family: monospace;
  }
`;

interface ProfileForm {
  id: string | null;
  name: string;
  description: string;
  active: boolean;
  permissions: MerchantPermissionCode[];
}

const EMPTY_PROFILE: ProfileForm = { id: null, name: '', description: '', active: true, permissions: [] };

/** Perfis de acesso e alçadas - cada funcionário é associado a um único perfil. */
const AccessProfilesPage: React.FC = () => {
  const { showNotification } = useNotification();

  const profilesState = useAsync(() => adminApi.listAccessProfiles(), []);
  const profiles = useMemo<MerchantAccessProfileDto[]>(() => profilesState.data ?? [], [profilesState.data]);

  const [search, setSearch] = useState<string>('');

  const filteredProfiles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return profiles;
    return profiles.filter((p) => {
      const inLabels = p.permissions.some(
        (c) => formatPermissionLabel(c).toLowerCase().includes(term) || c.toLowerCase().includes(term)
      );
      return p.name.toLowerCase().includes(term) || (p.description ?? '').toLowerCase().includes(term) || inLabels;
    });
  }, [profiles, search]);

  const stats = useMemo(() => {
    const active = profiles.filter((p) => p.active).length;
    const members = profiles.reduce((sum, p) => sum + (p.membersCount ?? 0), 0);
    return { available: ALL_CODES.length, total: profiles.length, active, members };
  }, [profiles]);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [form, setForm] = useState<ProfileForm>(EMPTY_PROFILE);
  const [saving, setSaving] = useState<boolean>(false);

  const openCreate = (): void => {
    setForm(EMPTY_PROFILE);
    setIsFormOpen(true);
  };
  const openEdit = (profile: MerchantAccessProfileDto): void => {
    setForm({
      id: profile.id,
      name: profile.name,
      description: profile.description ?? '',
      active: profile.active,
      permissions: [...profile.permissions],
    });
    setIsFormOpen(true);
  };
  const togglePermission = (code: MerchantPermissionCode): void => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(code)
        ? prev.permissions.filter((c) => c !== code)
        : [...prev.permissions, code],
    }));
  };

  const handleSubmitProfile = async (): Promise<void> => {
    if (saving) return;
    if (!form.id && !form.name.trim()) {
      showNotification('Informe o nome do perfil.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await adminApi.updateAccessProfilePermissions(form.id, { permissions: form.permissions });
        showNotification('Permissões do perfil atualizadas.', 'success');
      } else {
        await adminApi.createAccessProfile({
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          active: form.active,
          permissions: form.permissions,
        });
        showNotification('Perfil de acesso criado.', 'success');
      }
      setIsFormOpen(false);
      profilesState.reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o perfil.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderPermissionGrid = (): React.ReactNode =>
    PERMISSION_GROUP_ORDER.map((group) => {
      const codes = ALL_CODES.filter((c) => formatPermissionGroup(c) === group);
      if (codes.length === 0) return null;
      return (
        <div key={group}>
          <GroupTitle>{group}</GroupTitle>
          <PermissionsGrid>
            {codes.map((code) => {
              const active = form.permissions.includes(code);
              return (
                <PermissionCard key={code} $active={active} onClick={() => togglePermission(code)}>
                  <Checkbox checked={active} readOnly />
                  <div className="body">
                    <span className="title">{formatPermissionLabel(code)}</span>
                    <span className="desc">{formatPermissionDescription(code)}</span>
                    <span className="code">{code}</span>
                  </div>
                </PermissionCard>
              );
            })}
          </PermissionsGrid>
        </div>
      );
    });

  return (
    <>
      <PageHeader
        title="Perfis & Alçadas"
        subtitle="Perfis de acesso da loja. Cada funcionário é associado a um perfil que define suas permissões."
      />

      <Stats>
        <StatCard>
          <span>Permissões disponíveis</span>
          <strong>{stats.available}</strong>
        </StatCard>
        <StatCard>
          <span>Perfis cadastrados</span>
          <strong>{stats.total}</strong>
        </StatCard>
        <StatCard>
          <span>Perfis ativos</span>
          <strong>{stats.active}</strong>
        </StatCard>
        <StatCard>
          <span>Funcionários associados</span>
          <strong>{stats.members}</strong>
        </StatCard>
      </Stats>

      <Toolbar>
        <div style={{ flex: 1, maxWidth: 520 }}>
          <TextInput
            placeholder="Buscar por nome, descrição ou permissão..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button variant="primary" onClick={openCreate}>
          <Plus size={16} /> Novo perfil
        </Button>
      </Toolbar>

      <DataState
        loading={profilesState.loading}
        error={profilesState.error}
        onRetry={profilesState.reload}
        isEmpty={filteredProfiles.length === 0}
        emptyTitle="Nenhum perfil cadastrado"
        emptyDescription="Crie perfis de acesso para agilizar a concessão de permissões."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Perfil</th>
                <th>Descrição</th>
                <th>Permissões</th>
                <th>Funcionários</th>
                <th>Status</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {filteredProfiles.map((profile) => {
                const sorted = sortPermissionCodes(profile.permissions);
                return (
                  <tr key={profile.id}>
                    <td>
                      <strong>{profile.name}</strong>
                    </td>
                    <td>{profile.description || <Muted>-</Muted>}</td>
                    <td>
                      <BadgeRow>
                        <Badge tone="success">{profile.permissions.length} permissões</Badge>
                        {sorted.slice(0, 2).map((c) => (
                          <Badge key={c} tone="secondary">
                            {formatPermissionLabel(c)}
                          </Badge>
                        ))}
                        {profile.permissions.length > 2 && (
                          <Badge tone="secondary">+{profile.permissions.length - 2}</Badge>
                        )}
                      </BadgeRow>
                    </td>
                    <td>
                      <Badge tone={profile.membersCount ? 'info' : 'secondary'}>{profile.membersCount ?? 0}</Badge>
                    </td>
                    <td>
                      <Badge tone={profile.active ? 'success' : 'secondary'}>
                        {profile.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>
                      <RowActions>
                        <ActionButton type="button" aria-label="Editar perfil" onClick={() => openEdit(profile)}>
                          <Edit3 size={16} />
                        </ActionButton>
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        width="960px"
        title={form.id ? `Editar perfil: ${form.name}` : 'Novo perfil de acesso'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSubmitProfile} isLoading={saving}>
              {form.id ? 'Salvar permissões' : 'Criar perfil'}
            </Button>
          </>
        }
      >
        <Form>
          <TextInput
            label="Nome do perfil"
            value={form.name}
            disabled={!!form.id}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <TextInput
            label="Descrição"
            value={form.description}
            disabled={!!form.id}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          />
          {!form.id && (
            <Checkbox
              label="Perfil ativo"
              checked={form.active}
              onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
            />
          )}
          <div>{renderPermissionGrid()}</div>
        </Form>
      </Modal>
    </>
  );
};

export default AccessProfilesPage;
